import { useState } from 'react'
import { api, useAbruf, type ApiFehler } from '../api'
import { Fehleranzeige, Karte, Merkmal, Tabelle, Zustand } from '../bausteine'
import { datum } from '../formate'

interface Vermerk {
  kundennummer: string
  begruendung: string
}

interface Ergebnis {
  stichtag: string
  vorschau: boolean
  anonymisiert: Vermerk[]
  zurueckgestellt: Vermerk[]
}

/** Der heutige Tag als Wert für ein Datumsfeld. */
function heute(): string {
  return new Date().toISOString().slice(0, 10)
}

export default function Aufbewahrung() {
  const [stichtag, setzeStichtag] = useState(heute())
  const vorschau = useAbruf<Ergebnis>(`/api/aufbewahrung/vorschau?stichtag=${stichtag}`, [
    stichtag,
  ])

  const [laeuft, setzeLaeuft] = useState(false)
  const [ergebnis, setzeErgebnis] = useState<Ergebnis>()
  const [fehler, setzeFehler] = useState<ApiFehler>()

  async function durchfuehren() {
    setzeFehler(undefined)
    setzeErgebnis(undefined)
    setzeLaeuft(true)
    try {
      setzeErgebnis(
        await api.sende<Ergebnis>(`/api/aufbewahrung/laeufe?stichtag=${stichtag}`, {}),
      )
      vorschau.erneut()
    } catch (ursache) {
      setzeFehler(ursache as ApiFehler)
    } finally {
      setzeLaeuft(false)
    }
  }

  const faellig = vorschau.daten?.anonymisiert ?? []
  const zurueck = vorschau.daten?.zurueckgestellt ?? []

  return (
    <>
      <h1>Aufbewahrung und Löschung</h1>
      <p className="untertitel">
        Nach Ablauf der Aufbewahrungsfrist sind die personenbezogenen Angaben zu
        entfernen (Artikel 17 DSGVO). Der Stammsatz bleibt bestehen: an der Kundennummer
        hängen die Belege, die nach Paragraf 147 AO aufzubewahren sind.
      </p>

      <Karte titel="Stichtag">
        <div className="leiste">
          <div className="feld">
            <label htmlFor="stichtag">Stichtag</label>
            <input
              id="stichtag"
              type="date"
              value={stichtag}
              onChange={(ereignis) => setzeStichtag(ereignis.target.value)}
            />
          </div>
          <button className="warnung" disabled={laeuft || faellig.length === 0} onClick={durchfuehren}>
            {laeuft ? 'Läuft …' : `${faellig.length} Kunden löschen`}
          </button>
        </div>
        <p className="untertitel" style={{ marginBottom: 0 }}>
          Ein späterer Stichtag zeigt, wer bis dahin fällig wird. Die Anzeige allein
          ändert nichts; erst der Knopf führt den Lauf aus, und er lässt sich nicht
          zurückdrehen.
        </p>
      </Karte>

      {fehler && <Fehleranzeige fehler={fehler} />}
      {ergebnis && (
        <Karte titel="Durchgeführt">
          <p>
            Zum {datum(ergebnis.stichtag)} wurden {ergebnis.anonymisiert.length} Kunden
            anonymisiert, {ergebnis.zurueckgestellt.length} blieben zurückgestellt.
          </p>
        </Karte>
      )}

      <Karte titel={`Zum Stichtag fällig (${faellig.length})`}>
        <Zustand
          laeuft={vorschau.laeuft}
          fehler={vorschau.fehler}
          leer={faellig.length === 0}
          leertext="Kein Kunde ist zum Stichtag zu löschen."
        >
          <Tabelle spalten={[{ name: 'Kundennummer' }, { name: 'Grund' }]}>
            {faellig.map((vermerk) => (
              <tr key={vermerk.kundennummer}>
                <td>{vermerk.kundennummer}</td>
                <td>{vermerk.begruendung}</td>
              </tr>
            ))}
          </Tabelle>
        </Zustand>
      </Karte>

      <Karte titel={`Zurückgestellt (${zurueck.length})`}>
        <Zustand
          laeuft={vorschau.laeuft}
          fehler={vorschau.fehler}
          leer={zurueck.length === 0}
          leertext="Kein Kunde wird zurückgestellt."
        >
          <Tabelle spalten={[{ name: 'Kundennummer' }, { name: 'Hindernis' }]}>
            {zurueck.map((vermerk) => (
              <tr key={vermerk.kundennummer}>
                <td>{vermerk.kundennummer}</td>
                <td>
                  <Merkmal art="warnung">{vermerk.begruendung}</Merkmal>
                </td>
              </tr>
            ))}
          </Tabelle>
        </Zustand>
      </Karte>
    </>
  )
}
